/**
 * Single source of truth for every language the app supports.
 *
 * Adding a language is a one-line change here plus a `public/locales/<code>/`
 * folder (copy `en/` and translate) and a flag SVG at
 * `public/locales/flags/<code>.svg`. Nothing else needs to know about it —
 * the switcher, the detector's whitelist, `<html dir>` and date formatting
 * all derive from this list.
 */

import type { Locale as DateFnsLocale } from "date-fns";
import { ar as arDateFns, enUS as enDateFns } from "date-fns/locale";

export interface AppLocale {
  /** BCP-47 code — also the folder name under `public/locales`. */
  code: string;
  /** Name in the language itself, for the switcher. */
  label: string;
  /** Name in English, for admin/debug surfaces. */
  englishLabel: string;
  dir: "ltr" | "rtl";
  /** Drives localized `date-fns` output (weekday names, relative time…). */
  dateFnsLocale: DateFnsLocale;
}

export const APP_LOCALES: readonly AppLocale[] = [
  {
    code: "en",
    label: "English",
    englishLabel: "English",
    dir: "ltr",
    dateFnsLocale: enDateFns,
  },
  {
    code: "ar",
    label: "العربية",
    englishLabel: "Arabic",
    dir: "rtl",
    dateFnsLocale: arDateFns,
  },
] as const;

export const DEFAULT_LOCALE = "en";

/** Key under which the user's explicit choice is persisted. */
export const LOCALE_STORAGE_KEY = "maihrms.locale";

export const SUPPORTED_LOCALE_CODES = APP_LOCALES.map(({ code }) => code);

/**
 * Resolves any i18next language string (`ar`, `ar-AE`, `en-GB`, undefined) to a
 * locale we actually ship, falling back to the default.
 */
export const resolveLocale = (language?: string | null): AppLocale => {
  const base = (language ?? "").toLowerCase().split("-")[0];
  return (
    APP_LOCALES.find(({ code }) => code === base) ??
    APP_LOCALES.find(({ code }) => code === DEFAULT_LOCALE)!
  );
};

export const isRtl = (language?: string | null) => resolveLocale(language).dir === "rtl";
